import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Save, HelpCircle, ExternalLink } from "lucide-react";
import { adminListIntegrations, adminUpdateIntegrations, adminSetAiDefault } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useAuth } from "../../lib/auth";

// iter-62: keep long masked API keys on one line — truncate the middle
// so the prefix + last 4 chars are always visible.
function truncateMask(masked) {
    if (!masked) return "(unset)";
    const MAX = 44;
    if (masked.length <= MAX) return masked;
    return `${masked.slice(0, MAX - 7)}…${masked.slice(-4)}`;
}

export function IntegrationsSection() {
    const { user } = useAuth();
    const isReviewer = user?.role === "reviewer";
    const [items, setItems] = useState([]);
    const [values, setValues] = useState({});
    const [reveal, setReveal] = useState({});
    const [aiDefault, setAiDefault] = useState("auto");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const resp = await adminListIntegrations();
            setItems(resp.integrations || []);
            setAiDefault(resp.ai_default_provider || "auto");
            const next = {};
            for (const it of resp.integrations || []) next[it.key] = "";
            setValues(next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load integrations.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const handleSave = async () => {
        const payload = {};
        for (const [k, v] of Object.entries(values)) {
            if ((v || "").trim()) payload[k] = v.trim();
        }
        if (Object.keys(payload).length === 0) {
            toast.info("Nothing to save.");
            return;
        }
        setSaving(true);
        try {
            const resp = await adminUpdateIntegrations(payload);
            setItems(resp.integrations || []);
            setAiDefault(resp.ai_default_provider || aiDefault);
            const reset = {};
            for (const it of resp.integrations || []) reset[it.key] = "";
            setValues(reset);
            toast.success("Integrations updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async (key) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Clear the DB override? Service will fall back to the .env value.")) return;
        try {
            await adminUpdateIntegrations({ [key]: "" });
            load();
            toast.success("Cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear.");
        }
    };

    const handleSetDefault = async (providerId) => {
        try {
            const next = await adminSetAiDefault(providerId);
            setAiDefault(next);
            toast.success(
                providerId === "auto"
                    ? "Default: auto (env priority)"
                    : `Default provider set: ${providerId}`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't set default.");
        }
    };

    const aiItems = items.filter((i) => i.category === "ai");
    const serviceItems = items.filter((i) => i.category !== "ai");

    return (
        <section
            className="rounded-sm border border-[#2A3655] bg-[#0F1B3A] p-4 sm:p-5 space-y-5"
            data-testid="admin-integrations"
        >
            <div>
                <h2 className="text-[12px] uppercase tracking-[0.16em] text-slate-300 font-bold">
                    Integration keys
                </h2>
                <p className="mt-1 text-[12px] text-slate-500">
                    Stored in Mongo and layered over the container's{" "}
                    <code className="text-[11px] bg-[#0A1128] px-1 py-0.5 rounded border border-[#2A3655]">
                        .env
                    </code>
                    . Changes apply immediately — no backend restart needed.
                </p>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500">Loading…</div>
            ) : (
                <>
                    {aiItems.length > 0 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[12px] uppercase tracking-wider text-[#00A3FF] font-bold">
                                    AI providers
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                        Default:
                                    </span>
                                    <span
                                        className="text-[11px] font-mono text-emerald-300 uppercase"
                                        data-testid="ai-default-label"
                                    >
                                        {aiDefault}
                                    </span>
                                </div>
                            </div>
                            <div className="rounded-sm border border-[#1C253C] bg-[#0A1128]">
                                <label
                                    className={`flex items-center gap-2 px-3 py-2 border-b border-[#1C253C] ${
                                        isReviewer
                                            ? "cursor-not-allowed opacity-60"
                                            : "cursor-pointer hover:bg-[#1C253C]"
                                    }`}
                                    data-testid="ai-default-auto"
                                >
                                    <input
                                        type="radio"
                                        name="ai-default"
                                        checked={aiDefault === "auto"}
                                        onChange={() => !isReviewer && handleSetDefault("auto")}
                                        disabled={isReviewer}
                                        className="accent-[#00A3FF]"
                                    />
                                    <span className="text-[12px] font-bold text-white">
                                        Auto
                                    </span>
                                    <span className="text-[11px] text-slate-500">
                                        — pick the first configured provider in this order: Anthropic → Gemini → OpenAI → Emergent key.
                                    </span>
                                </label>
                                {aiItems.map((it) => (
                                    <IntegrationRow
                                        key={it.key}
                                        item={it}
                                        value={values[it.key] || ""}
                                        reveal={!!reveal[it.key]}
                                        isReviewer={isReviewer}
                                        onChangeValue={(v) =>
                                            setValues({ ...values, [it.key]: v })
                                        }
                                        onToggleReveal={() =>
                                            setReveal({
                                                ...reveal,
                                                [it.key]: !reveal[it.key],
                                            })
                                        }
                                        onClear={() => handleClear(it.key)}
                                        isDefault={
                                            it.provider_id &&
                                            it.provider_id === aiDefault
                                        }
                                        canBeDefault={it.default_eligible}
                                        onMakeDefault={() =>
                                            handleSetDefault(it.provider_id)
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {serviceItems.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-[12px] uppercase tracking-wider text-slate-400 font-bold">
                                Services
                            </h3>
                            <div className="rounded-sm border border-[#1C253C] bg-[#0A1128]">
                                {serviceItems.map((it) => (
                                    <IntegrationRow
                                        key={it.key}
                                        item={it}
                                        value={values[it.key] || ""}
                                        reveal={!!reveal[it.key]}
                                        isReviewer={isReviewer}
                                        onChangeValue={(v) =>
                                            setValues({ ...values, [it.key]: v })
                                        }
                                        onToggleReveal={() =>
                                            setReveal({
                                                ...reveal,
                                                [it.key]: !reveal[it.key],
                                            })
                                        }
                                        onClear={() => handleClear(it.key)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3">
                        {isReviewer && (
                            <span
                                className="text-[11px] text-amber-300/90 font-bold"
                                data-testid="integrations-reviewer-notice"
                            >
                                Read-only for reviewer accounts
                            </span>
                        )}
                        <Button
                            onClick={handleSave}
                            disabled={saving || isReviewer}
                            data-testid="integrations-save"
                            className="bg-[#007AFF] hover:bg-[#0062cc] disabled:opacity-40"
                        >
                            <Save className="w-3.5 h-3.5 mr-1" />
                            Save overrides
                        </Button>
                    </div>
                </>
            )}
        </section>
    );
}

export function IntegrationRow({
    item,
    value,
    reveal,
    onChangeValue,
    onToggleReveal,
    onClear,
    isDefault = false,
    canBeDefault = false,
    onMakeDefault,
    isReviewer = false,
}) {
    const it = item;
    const handleRevealClick = () => {
        if (isReviewer) {
            // eslint-disable-next-line no-alert
            window.alert("Sorry, that isn't visible to you.");
            return;
        }
        onToggleReveal();
    };
    return (
        <div
            className={`border-b border-[#1C253C] last:border-0 p-3 ${
                isDefault ? "bg-emerald-500/[0.04]" : ""
            }`}
            data-testid={`integration-row-${it.key}`}
        >
            <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-[13px] font-semibold text-white">
                            {it.label}
                        </div>
                        {it.help && <HelpPopover help={it.help} testKey={it.key} />}
                        {isDefault && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-emerald-500/60 text-emerald-300 bg-emerald-500/10">
                                Default
                            </span>
                        )}
                        {canBeDefault && !isDefault && it.has_value && (
                            <button
                                type="button"
                                onClick={onMakeDefault}
                                data-testid={`integration-make-default-${it.key}`}
                                className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-[#2A3655] text-slate-400 hover:bg-[#1C253C] hover:border-[#00A3FF] hover:text-[#00A3FF]"
                            >
                                Set default
                            </button>
                        )}
                        {it.provider_id && !it.default_eligible && (
                            <span className="text-[9px] uppercase tracking-wider text-amber-300/80 font-semibold">
                                not wired yet
                            </span>
                        )}
                    </div>
                    {it.hint && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            {it.hint}
                        </div>
                    )}
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1 flex items-baseline gap-1 min-w-0">
                        <span className="shrink-0">Current:</span>
                        <span
                            className="text-slate-300 font-mono normal-case truncate max-w-full min-w-0 flex-1"
                            title={it.masked || "(unset)"}
                            data-testid={`integration-current-${it.key}`}
                        >
                            {truncateMask(it.masked)}
                        </span>
                        <span className="shrink-0">· source:</span>
                        <span
                            className={`font-bold shrink-0 ${
                                it.source === "db"
                                    ? "text-emerald-300"
                                    : it.source === "env"
                                        ? "text-[#6EB2FF]"
                                        : "text-red-300"
                            }`}
                        >
                            {it.source}
                        </span>
                    </div>
                </div>
                {it.source === "db" && !isReviewer && (
                    <button
                        type="button"
                        onClick={onClear}
                        data-testid={`integration-clear-${it.key}`}
                        className="text-[11px] text-slate-400 hover:text-red-300 shrink-0"
                    >
                        Clear override
                    </button>
                )}
            </div>
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Input
                        type={it.secret && !reveal ? "password" : "text"}
                        value={value}
                        onChange={(e) => onChangeValue(e.target.value)}
                        placeholder={
                            isReviewer
                                ? "Read-only for reviewer accounts"
                                : it.has_value
                                    ? "Enter new value to replace"
                                    : "Enter value"
                        }
                        disabled={isReviewer}
                        data-testid={`integration-input-${it.key}`}
                        className="bg-[#0F1B3A] border-[#2A3655] text-white pr-9 font-mono text-[12px] disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    {it.secret && (
                        <button
                            type="button"
                            onClick={handleRevealClick}
                            data-testid={`integration-reveal-${it.key}`}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                            aria-label={
                                isReviewer
                                    ? "Hidden for reviewer accounts"
                                    : reveal
                                        ? "Hide value"
                                        : "Show value"
                            }
                        >
                            {reveal ? (
                                <EyeOff className="w-3.5 h-3.5" />
                            ) : (
                                <Eye className="w-3.5 h-3.5" />
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


/**
 * Superadmin view of client-side errors auto-reported by
 * `RouteErrorBoundary` (currently wrapping the Workspace route). Newest
 * first, 200 most recent. Each row expands to show the full stack +
 * component stack so you can triage without asking the user for a
 * screenshot.
 */

function HelpPopover({ help, testKey }) {
    if (!help) return null;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`integration-help-${testKey}`}
                    aria-label="How to obtain this credential"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-[#00A3FF] hover:bg-[#1C253C] transition-colors"
                >
                    <HelpCircle className="w-3.5 h-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="bottom"
                className="w-[360px] bg-[#0A1128] border border-[#2A3655] text-slate-200 p-4 rounded-sm shadow-xl"
                data-testid={`integration-help-content-${testKey}`}
            >
                <div className="text-[12px] uppercase tracking-[0.14em] font-bold text-[#00A3FF] mb-2">
                    {help.title || "How to obtain this"}
                </div>
                <ol className="list-decimal pl-4 space-y-1.5 text-[12px] text-slate-300 leading-snug">
                    {(help.steps || []).map((step, i) => (
                        <li key={i}>{step}</li>
                    ))}
                </ol>
                {help.link && (
                    <a
                        href={help.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-semibold text-[#00A3FF] hover:text-[#6EB2FF] hover:underline"
                    >
                        Open console
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </PopoverContent>
        </Popover>
    );
}
